import { Button, Input, Select, CircleX } from "../../../ui";
import { Checkbox } from "../../../ui/Checkbox";
import "./FormFieldBuilder.css";

export default function FormFieldBuilder({ fields, setFields }) {
  const addField = () => {
    setFields([
      ...fields,
      {
        key: `f-${Date.now()}`,
        label: {
          ar: "",
          en: "",
        },
        type: "text",
        required: false,
        options: {
          ar: [],
          en: [],
        },
      },
    ]);
  };

  // Immutable: the old version mutated the field objects in place, which are
  // shared with the loaded category list, so Cancel didn't undo edits.
  const updateField = (index, key, value) => {
    setFields(
      fields.map((field, i) => (i === index ? { ...field, [key]: value } : field)),
    );
  };

  // Server fields may have no `options`; blur used to crash on them.
  const optionsOf = (field) => ({
    ar: [...(field.options?.ar || [])],
    en: [...(field.options?.en || [])],
  });

  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  return (
    <div className="field-builder">
      <div className="field-builder-header">
        <h4 className="field-builder-title">Custom Fields</h4>

        <Button type="button" variant="primary" appearance="soft" size="sm" onClick={addField}>
          + Add Field
        </Button>
      </div>

      {fields.length === 0 && (
        <div className="field-builder-empty">No custom fields added yet.</div>
      )}

      {fields.map((field, index) => (
        // Keyed by the field's own key: with index keys, removing a field left
        // the (uncontrolled) options boxes showing the removed field's values.
        <div key={field.key || index} className="field-builder-card">
          <div className="field-builder-grid">
            <div className="field-builder-group">
              <Input
                label="Label (Arabic)"
                value={field.label?.ar || ""}
                onChange={(e) =>
                  updateField(index, "label", {
                    ...field.label,
                    ar: e.target.value,
                  })
                }
              />
            </div>

            <div className="field-builder-group">
              <Input
                label="Label (English)"
                value={field.label?.en || ""}
                onChange={(e) =>
                  updateField(index, "label", {
                    ...field.label,
                    en: e.target.value,
                  })
                }
              />
            </div>

            <div className="field-builder-group">
              <Select
                label="Field Type"
                value={field.type}
                options={[
                  { value: "text", label: "Text" },
                  { value: "textarea", label: "Textarea" },
                  { value: "number", label: "Number" },
                  { value: "date", label: "Date" },
                  { value: "time", label: "Time" },
                  { value: "select", label: "Select" },
                ]}
                onChange={(e) => updateField(index, "type", e.target.value)}
              />
            </div>

            <div className="field-builder-actions">
              <Button
                type="button"
                variant="danger"
                appearance="soft"
                size="sm"
                rounded
                icon={<CircleX size={16} />}
                aria-label={`Remove field ${field.label?.en || field.label?.ar || index + 1}`}
                title="Remove field"
                onClick={() => removeField(index)}
              />
            </div>
          </div>
          <Checkbox
            label="Required"
            checked={field.required}
            onChange={(e) => updateField(index, "required", e.target.checked)}
          />

          {field.type === "select" && (
            <div className="field-builder-options">
              <div className="field-builder-group">
                <Input
                  textarea
                  label="Select Options (Arabic)"
                  defaultValue={field.options?.ar?.join(", ")}
                  onBlur={(e) =>
                    updateField(index, "options", {
                      ...optionsOf(field),
                      ar: e.target.value
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </div>

              <div className="field-builder-group">
                <Input
                  textarea
                  label="Select Options (English)"
                  defaultValue={field.options?.en?.join(", ")}
                  onBlur={(e) =>
                    updateField(index, "options", {
                      ...optionsOf(field),
                      en: e.target.value
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
